import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

const ACCORDS = ['Woody', 'Oriental', 'Fresh', 'Floral', 'Citrus', 'Aromatic', 'Aquatic', 'Spicy', 'Gourmand', 'Leather', 'Amber', 'Earthy', 'Powdery', 'Fruity'];
const NOTES = ['Vanilla', 'Oud', 'Sandalwood', 'Rose', 'Jasmine', 'Cedar', 'Bergamot', 'Patchouli', 'Musk', 'Amber', 'Tobacco', 'Leather', 'Vetiver', 'Lavender', 'Pepper', 'Citrus', 'Coconut', 'Iris', 'Neroli', 'Incense'];
const OCCASIONS = ['Daily / Casual', 'Office / Work', 'Evenings Out', 'Dates', 'Sport / Gym', 'Special Occasions', 'Night Out'];
const HOUSES = [
  'Chanel', 'Dior', 'Tom Ford', 'Creed', 'Maison Margiela', 'Yves Saint Laurent',
  'Givenchy', 'Hermès', 'Guerlain', 'Paco Rabanne', 'Jean Paul Gaultier', 'Versace',
  'Armani', 'Hugo Boss', 'Calvin Klein', 'Dolce & Gabbana', 'Burberry', 'Valentino',
  'Amouage', 'Byredo', 'Diptyque', 'Jo Malone', 'Maison Francis Kurkdjian',
  'Parfums de Marly', 'Xerjoff', 'Initio', 'Nishane', 'Roja Parfums',
  'Le Labo', 'Serge Lutens', 'Frederic Malle', 'Acqua di Parma',
  'Penhaligons', 'Floris', 'Molton Brown', 'Comme des Garçons',
  'Other',
];

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handle = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return isMobile;
};

const Profile = ({ token, onUsernameChange }) => {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);

  // Preferences
  const [likedAccords, setLikedAccords] = useState([]);
  const [likedNotes, setLikedNotes] = useState([]);
  const [dislikedNotes, setDislikedNotes] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [strength, setStrength] = useState('');
  const [budget, setBudget] = useState('');
  const [preferredGender, setPreferredGender] = useState('');
  const [prefSaved, setPrefSaved] = useState('');
  const [prefError, setPrefError] = useState('');

  // Account
  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  const [favouriteHouse, setFavouriteHouse] = useState('');
  const [accountSaved, setAccountSaved] = useState('');
  const [accountError, setAccountError] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState('');

  // Email change
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailSaved, setEmailSaved] = useState('');
  const [emailError, setEmailError] = useState('');

  // Password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSaved, setPwSaved] = useState('');
  const [pwError, setPwError] = useState('');

  // Delete account
  const [deleteStep, setDeleteStep] = useState(0);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const resetDelete = () => { setDeleteStep(0); setDeleteConfirmText(''); setDeletePassword(''); setDeleteError(''); };

  useEffect(() => {
    fetchProfile();
    const params = new URLSearchParams(window.location.search);
    if (params.get('emailChanged')) setEmailSaved('Email updated successfully');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/api/profile`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
      const p = res.data.profile;
      const u = res.data.user;
      if (p) {
        setLikedAccords(p.favourite_accords || []);
        setLikedNotes(p.favourite_notes || []);
        setDislikedNotes(p.disliked_notes || []);
        setOccasions(p.occasions || []);
        setStrength(p.fragrance_strength || '');
        setBudget(p.budget || '');
        setPreferredGender(p.preferred_gender || '');
      }
      if (u) {
        setUsername(u.username || '');
        setBio(u.profile_bio || '');
        setFavouriteHouse(u.favourite_house || '');
        setAvatarUrl(u.profile_picture_url || '');
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // Standard toggle (for accords, occasions)
  const toggle = (list, setList, item) =>
    setList(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);

  // Note toggle with conflict prevention — removes from the other list if present
  const toggleNote = (key, value) => {
    if (key === 'liked') {
      if (dislikedNotes.includes(value)) return; // blocked
      setLikedNotes(prev => prev.includes(value) ? prev.filter(n => n !== value) : [...prev, value]);
    } else {
      if (likedNotes.includes(value)) return; // blocked
      setDislikedNotes(prev => prev.includes(value) ? prev.filter(n => n !== value) : [...prev, value]);
    }
  };

  // Select all occasions
  const allOccasionsSelected = OCCASIONS.every(o => occasions.includes(o));
  const handleAllOccasions = () => setOccasions(allOccasionsSelected ? [] : [...OCCASIONS]);

  const savePreferences = async () => {
    setPrefSaved(''); setPrefError('');
    try {
      await axios.patch(`${API}/api/profile/preferences`, {
        favourite_accords: likedAccords,
        favourite_notes: likedNotes,
        disliked_notes: dislikedNotes,
        occasions,
        fragrance_strength: strength,
        budget,
        preferred_gender: preferredGender,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setPrefSaved('Preferences saved');
      setTimeout(() => setPrefSaved(''), 3000);
    } catch { setPrefError('Failed to save'); }
  };

  const saveAccount = async () => {
    setAccountSaved(''); setAccountError('');
    try {
      const res = await axios.patch(`${API}/api/profile/account`, {
        username, bio, favourite_house: favouriteHouse,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setAccountSaved('Profile updated');
      setData(prev => ({ ...prev, user: { ...prev.user, ...res.data } }));
      if (onUsernameChange) onUsernameChange(res.data.username);
      setTimeout(() => setAccountSaved(''), 3000);
    } catch (err) { setAccountError(err.response?.data?.error || 'Failed to save'); }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      const res = await axios.post(`${API}/api/profile/avatar`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setAvatarUrl(res.data.url);
      setData(prev => ({ ...prev, user: { ...prev.user, profile_picture_url: res.data.url } }));
    } catch (err) { setAccountError('Upload failed'); }
    setAvatarUploading(false);
  };

  const saveEmail = async () => {
    setEmailSaved(''); setEmailError('');
    if (!newEmail || !emailPassword) return setEmailError('Please fill in all fields');
    try {
      await axios.patch(`${API}/api/profile/email`, { new_email: newEmail, password: emailPassword }, { headers: { Authorization: `Bearer ${token}` } });
      setEmailSaved('Verification email sent — check your inbox');
      setNewEmail(''); setEmailPassword('');
      setTimeout(() => setEmailSaved(''), 6000);
    } catch (err) { setEmailError(err.response?.data?.error || 'Failed to update'); }
  };

  const savePassword = async () => {
    setPwSaved(''); setPwError('');
    if (newPassword !== confirmPassword) return setPwError('Passwords do not match');
    if (newPassword.length < 6) return setPwError('Minimum 6 characters');
    try {
      await axios.patch(`${API}/api/profile/password`, { current_password: currentPassword, new_password: newPassword }, { headers: { Authorization: `Bearer ${token}` } });
      setPwSaved('Password updated');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      setTimeout(() => setPwSaved(''), 3000);
    } catch (err) { setPwError(err.response?.data?.error || 'Failed to update'); }
  };

  const handleDeleteAccount = async () => {
    setDeleteError(''); setDeleting(true);
    try {
      await axios.delete(`${API}/api/profile/account`, {
        data: { password: deletePassword },
        headers: { Authorization: `Bearer ${token}` },
      });
      localStorage.removeItem('token');
      localStorage.removeItem('onboarding_completed');
      window.location.href = '/';
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'Failed to delete account');
      setDeleting(false);
    }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: '#3a3a3a', letterSpacing: '0.3em', fontSize: '0.7rem', fontFamily: 'Jost, sans-serif' }}>LOADING</p>
    </div>
  );

  const { user, stats } = data || {};
  const avatarColor = user?.avatar_color || '#c9a96e';
  const initials = user?.username ? user.username.slice(0, 2).toUpperCase() : '??';

  const s = {
    page: { minHeight: '100vh', background: '#0a0a0a', paddingTop: isMobile ? '88px' : '100px', paddingBottom: '80px', fontFamily: 'Jost, sans-serif' },
    container: { maxWidth: '860px', margin: '0 auto', padding: isMobile ? '0 16px' : '0 32px' },
    eyebrow: { fontSize: '0.65rem', letterSpacing: '0.4em', textTransform: 'uppercase', color: '#c9a96e', marginBottom: '10px', fontWeight: 400 },
    pageTitle: { fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '2rem' : '3rem', fontWeight: 300, color: '#f5f0eb', marginBottom: '32px', lineHeight: 1 },
    tabs: { display: 'flex', borderBottom: '1px solid #1e1e1e', marginBottom: '40px', overflowX: 'auto' },
    tab: { padding: isMobile ? '12px 20px' : '14px 32px', fontSize: '0.65rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#6b6460', cursor: 'pointer', border: 'none', background: 'none', fontFamily: 'Jost, sans-serif', borderBottom: '2px solid transparent', marginBottom: '-1px', transition: 'all 0.2s ease', whiteSpace: 'nowrap' },
    tabActive: { padding: isMobile ? '12px 20px' : '14px 32px', fontSize: '0.65rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#c9a96e', cursor: 'pointer', border: 'none', background: 'none', fontFamily: 'Jost, sans-serif', borderBottom: '2px solid #c9a96e', marginBottom: '-1px', transition: 'all 0.2s ease', whiteSpace: 'nowrap' },
    statsGrid: { display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '1px', background: '#1a1a1a', marginBottom: '1px' },
    statCard: { background: '#0f0f0f', padding: isMobile ? '24px 16px' : '32px 20px', textAlign: 'center' },
    statValue: { fontFamily: 'Cormorant Garamond, serif', fontSize: isMobile ? '2rem' : '2.6rem', fontWeight: 300, color: '#c9a96e', lineHeight: 1, marginBottom: '10px' },
    statValueSm: { fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem', fontWeight: 300, color: '#c9a96e', lineHeight: 1, marginBottom: '10px', paddingTop: '8px' },
    statLabel: { fontSize: '0.58rem', letterSpacing: '0.22em', textTransform: 'uppercase', color: '#6b6460' },
    infoGrid: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1px', background: '#1a1a1a', marginTop: '1px' },
    infoCell: { background: '#0f0f0f', padding: isMobile ? '18px 16px' : '22px 24px' },
    infoLabel: { fontSize: '0.58rem', letterSpacing: '0.22em', textTransform: 'uppercase', color: '#6b6460', marginBottom: '7px' },
    infoValue: { fontSize: '1.05rem', color: '#f5f0eb', fontWeight: 300, fontFamily: 'Cormorant Garamond, serif' },
    badge: { display: 'inline-block', fontSize: '0.55rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: '#c9a96e', border: '1px solid rgba(201,169,110,0.35)', padding: '4px 10px' },
    prefSection: { marginBottom: '40px' },
    prefLabel: { fontSize: '0.62rem', letterSpacing: '0.28em', textTransform: 'uppercase', color: '#6b6460', marginBottom: '14px', display: 'block', paddingBottom: '10px', borderBottom: '1px solid #161616' },
    tagsGrid: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
    tag: { padding: '8px 16px', fontSize: '0.68rem', letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer', border: '1px solid #2a2a2a', background: '#0f0f0f', color: '#6b6460', transition: 'all 0.15s ease', fontFamily: 'Jost, sans-serif' },
    tagActive: { padding: '8px 16px', fontSize: '0.68rem', letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer', border: '1px solid #c9a96e', background: 'rgba(201, 169, 110, 0.08)', color: '#c9a96e', transition: 'all 0.15s ease', fontFamily: 'Jost, sans-serif' },
    tagDisliked: { padding: '8px 16px', fontSize: '0.68rem', letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer', border: '1px solid #7a4040', background: 'rgba(180, 80, 80, 0.08)', color: '#c07070', transition: 'all 0.15s ease', fontFamily: 'Jost, sans-serif' },
    tagDisabled: { padding: '8px 16px', fontSize: '0.68rem', letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'not-allowed', border: '1px solid #1a1a1a', background: '#0d0d0d', color: '#2a2a2a', transition: 'all 0.15s ease', fontFamily: 'Jost, sans-serif', opacity: 0.4 },
    allOccasionsBtn: (selected) => ({
      padding: '8px 16px', fontSize: '0.68rem', letterSpacing: '0.12em', textTransform: 'uppercase',
      cursor: 'pointer', fontFamily: 'Jost, sans-serif', marginBottom: '8px',
      border: `1px solid ${selected ? '#c9a96e' : '#2a2a2a'}`,
      background: selected ? 'rgba(201,169,110,0.08)' : '#0f0f0f',
      color: selected ? '#c9a96e' : '#665e58',
    }),
    selectRow: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '12px' },
    selectGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
    select: { background: '#0f0f0f', border: '1px solid #2a2a2a', color: '#f5f0eb', padding: '12px 16px', fontFamily: 'Jost, sans-serif', fontSize: '0.78rem', fontWeight: 300, outline: 'none', width: '100%', cursor: 'pointer' },
    saveBtn: { background: '#c9a96e', border: 'none', padding: '14px 40px', color: '#0a0a0a', fontFamily: 'Jost, sans-serif', fontSize: '0.68rem', fontWeight: 400, letterSpacing: '0.22em', textTransform: 'uppercase', cursor: 'pointer', marginTop: '32px', width: isMobile ? '100%' : 'auto' },
    input: { width: '100%', background: '#0f0f0f', border: '1px solid #2a2a2a', padding: '14px 16px', color: '#f5f0eb', fontFamily: 'Jost, sans-serif', fontSize: '0.85rem', fontWeight: 300, outline: 'none', marginBottom: '12px', boxSizing: 'border-box', maxWidth: isMobile ? '100%' : '480px', display: 'block' },
    textarea: { width: '100%', background: '#0f0f0f', border: '1px solid #2a2a2a', padding: '14px 16px', color: '#f5f0eb', fontFamily: 'Jost, sans-serif', fontSize: '0.85rem', fontWeight: 300, outline: 'none', marginBottom: '12px', boxSizing: 'border-box', maxWidth: isMobile ? '100%' : '480px', display: 'block', resize: 'vertical', height: '90px' },
    outlineBtn: { background: 'transparent', border: '1px solid #3a3a3a', padding: '14px 40px', color: '#a09890', fontFamily: 'Jost, sans-serif', fontSize: '0.68rem', fontWeight: 400, letterSpacing: '0.22em', textTransform: 'uppercase', cursor: 'pointer', marginTop: '8px', width: isMobile ? '100%' : 'auto' },
    dangerBtn: { background: 'transparent', border: '1px solid #7a3030', padding: '14px 40px', color: '#e07070', fontFamily: 'Jost, sans-serif', fontSize: '0.68rem', fontWeight: 400, letterSpacing: '0.22em', textTransform: 'uppercase', cursor: 'pointer', marginTop: '8px', width: isMobile ? '100%' : 'auto' },
    successMsg: { color: '#7aad7a', fontSize: '0.72rem', letterSpacing: '0.1em', marginTop: '14px' },
    errorMsg: { color: '#e07070', fontSize: '0.72rem', letterSpacing: '0.1em', marginTop: '14px' },
    sectionDivider: { borderTop: '1px solid #1a1a1a', margin: '40px 0' },
    sectionTitle: { fontSize: '0.62rem', letterSpacing: '0.28em', textTransform: 'uppercase', color: '#6b6460', marginBottom: '20px', display: 'block', paddingBottom: '10px', borderBottom: '1px solid #161616' },
    securityNote: { fontSize: '0.72rem', color: '#4a4a4a', letterSpacing: '0.05em', lineHeight: 1.7, marginBottom: '20px' },
    smallLabel: { fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#4a4a4a', marginBottom: '8px', display: 'block' },
  };

  return (
    <div style={s.page}>
      <div style={s.container}>

        {/* Avatar + title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '32px' }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            {avatarUrl ? (
              <img src={avatarUrl} alt="avatar" style={{ width: '64px', height: '64px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #2a2a2a' }} />
            ) : (
              <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: avatarColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem', fontWeight: 300, color: '#0a0a0a', border: '1px solid #2a2a2a' }}>
                {initials}
              </div>
            )}
          </div>
          <div>
            <p style={s.eyebrow}>Account</p>
            <h1 style={{ ...s.pageTitle, marginBottom: 0 }}>{user?.username?.toUpperCase()}</h1>
          </div>
        </div>

        <div style={s.tabs}>
          {['overview', 'profile', 'preferences', 'security'].map(tab => (
            <button key={tab} style={activeTab === tab ? s.tabActive : s.tab} onClick={() => setActiveTab(tab)}>{tab}</button>
          ))}
        </div>

        {/* ── Overview ── */}
        {activeTab === 'overview' && (
          <div>
            <div style={s.statsGrid}>
              <div style={s.statCard}><div style={s.statValue}>{stats?.total_fragrances || 0}</div><div style={s.statLabel}>Fragrances</div></div>
              <div style={s.statCard}><div style={s.statValue}>£{Math.round(stats?.total_value || 0)}</div><div style={s.statLabel}>Est. Value</div></div>
              <div style={s.statCard}><div style={s.statValue}>{stats?.most_worn?.wear_count || 0}</div><div style={s.statLabel}>Total Wears</div></div>
              <div style={s.statCard}><div style={stats?.favourite_accord ? s.statValueSm : s.statValue}>{stats?.favourite_accord?.split(' ')[0] || '—'}</div><div style={s.statLabel}>Top Accord</div></div>
            </div>
            <div style={s.infoGrid}>
              <div style={s.infoCell}><div style={s.infoLabel}>Most Worn</div><div style={s.infoValue}>{stats?.most_worn ? `${stats.most_worn.brand} ${stats.most_worn.name}` : '—'}</div></div>
              <div style={s.infoCell}><div style={s.infoLabel}>Favourite Accord</div><div style={s.infoValue}>{stats?.favourite_accord || '—'}</div></div>
              <div style={s.infoCell}><div style={s.infoLabel}>Favourite House</div><div style={s.infoValue}>{user?.favourite_house || '—'}</div></div>
              <div style={s.infoCell}><div style={s.infoLabel}>Email</div><div style={s.infoValue}>{user?.email}</div></div>
              <div style={s.infoCell}><div style={s.infoLabel}>Member Since</div><div style={s.infoValue}>{user?.created_at ? new Date(user.created_at).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) : '—'}</div></div>
              <div style={s.infoCell}><div style={s.infoLabel}>Account Type</div><span style={s.badge}>{user?.is_admin ? 'Admin' : 'Member'}</span></div>
              {user?.profile_bio && <div style={{ ...s.infoCell, gridColumn: isMobile ? '1' : '1 / -1' }}><div style={s.infoLabel}>Bio</div><div style={{ ...s.infoValue, fontSize: '0.85rem', fontFamily: 'Jost, sans-serif', fontWeight: 300, color: '#a09890', lineHeight: 1.6 }}>{user.profile_bio}</div></div>}
            </div>
          </div>
        )}

        {/* ── Profile ── */}
        {activeTab === 'profile' && (
          <div>
            <label style={s.sectionTitle}>Profile Picture</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '32px' }}>
              {avatarUrl ? (
                <img src={avatarUrl} alt="avatar" style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #2a2a2a' }} />
              ) : (
                <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: avatarColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Cormorant Garamond, serif', fontSize: '1.8rem', fontWeight: 300, color: '#0a0a0a' }}>
                  {initials}
                </div>
              )}
              <div>
                <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarChange} />
                <button style={s.outlineBtn} onClick={() => fileInputRef.current.click()} disabled={avatarUploading}>
                  {avatarUploading ? 'Uploading...' : 'Upload Photo'}
                </button>
                <p style={{ ...s.securityNote, marginTop: '8px', marginBottom: 0 }}>JPG or PNG, max 5MB. Auto-cropped to square.</p>
              </div>
            </div>
            <div style={s.sectionDivider} />
            <label style={s.sectionTitle}>Username</label>
            <label style={s.smallLabel}>Username</label>
            <input style={s.input} type="text" value={username} onChange={e => setUsername(e.target.value)} placeholder="Your username" />
            <div style={s.sectionDivider} />
            <label style={s.sectionTitle}>About Me</label>
            <label style={s.smallLabel}>Bio</label>
            <textarea style={s.textarea} value={bio} onChange={e => setBio(e.target.value)} placeholder="A few words about your fragrance journey..." maxLength={300} />
            <p style={{ ...s.securityNote, marginBottom: '0' }}>{bio.length}/300</p>
            <div style={s.sectionDivider} />
            <label style={s.sectionTitle}>Favourite Fragrance House</label>
            <select style={{ ...s.select, maxWidth: isMobile ? '100%' : '480px', marginBottom: '0' }} value={favouriteHouse} onChange={e => setFavouriteHouse(e.target.value)}>
              <option value="">Select a house...</option>
              {HOUSES.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
            <div style={s.sectionDivider} />
            <button style={s.saveBtn} onClick={saveAccount}>Save Profile</button>
            {accountSaved && <p style={s.successMsg}>{accountSaved}</p>}
            {accountError && <p style={s.errorMsg}>{accountError}</p>}
          </div>
        )}

        {/* ── Preferences ── */}
        {activeTab === 'preferences' && (
          <div>

            {/* Accords */}
            <div style={s.prefSection}>
              <label style={s.prefLabel}>Favourite Accords</label>
              <div style={s.tagsGrid}>
                {ACCORDS.map(a => (
                  <button key={a} style={likedAccords.includes(a) ? s.tagActive : s.tag} onClick={() => toggle(likedAccords, setLikedAccords, a)}>{a}</button>
                ))}
              </div>
            </div>

            {/* Favourite Notes — disabled if already disliked */}
            <div style={s.prefSection}>
              <label style={s.prefLabel}>Favourite Notes</label>
              <div style={s.tagsGrid}>
                {NOTES.map(n => {
                  const isDisliked = dislikedNotes.includes(n);
                  const isLiked = likedNotes.includes(n);
                  return (
                    <button
                      key={n}
                      style={isLiked ? s.tagActive : isDisliked ? s.tagDisabled : s.tag}
                      onClick={() => toggleNote('liked', n)}
                      title={isDisliked ? 'Remove from disliked notes first' : ''}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Disliked Notes — disabled if already liked */}
            <div style={s.prefSection}>
              <label style={s.prefLabel}>Disliked Notes</label>
              <div style={s.tagsGrid}>
                {NOTES.map(n => {
                  const isLiked = likedNotes.includes(n);
                  const isDisliked = dislikedNotes.includes(n);
                  return (
                    <button
                      key={n}
                      style={isDisliked ? s.tagDisliked : isLiked ? s.tagDisabled : s.tag}
                      onClick={() => toggleNote('disliked', n)}
                      title={isLiked ? 'Remove from favourite notes first' : ''}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Occasions with select all */}
            <div style={s.prefSection}>
              <label style={s.prefLabel}>Occasions</label>
              <div style={{ marginBottom: '8px' }}>
                <button style={s.allOccasionsBtn(allOccasionsSelected)} onClick={handleAllOccasions}>
                  ✦ All Occasions — I wear fragrance every day
                </button>
              </div>
              <div style={s.tagsGrid}>
                {OCCASIONS.map(o => (
                  <button key={o} style={occasions.includes(o) ? s.tagActive : s.tag} onClick={() => toggle(occasions, setOccasions, o)}>{o}</button>
                ))}
              </div>
            </div>

            {/* Dropdowns */}
            <div style={{ ...s.prefSection, marginTop: '32px' }}>
              <label style={s.prefLabel}>Settings</label>
              <div style={s.selectRow}>
                <div style={s.selectGroup}>
                  <span style={s.smallLabel}>Strength</span>
                  <select style={s.select} value={strength} onChange={e => setStrength(e.target.value)}>
                    <option value="">Select...</option>
                    <option value="Subtle">Subtle</option>
                    <option value="Moderate">Moderate</option>
                    <option value="Bold">Bold</option>
                  </select>
                </div>
                <div style={s.selectGroup}>
                  <span style={s.smallLabel}>Budget</span>
                  <select style={s.select} value={budget} onChange={e => setBudget(e.target.value)}>
                    <option value="">Select...</option>
                    <option value="budget">Under £30</option>
                    <option value="mid">£30 – £80</option>
                    <option value="premium">£80 – £200</option>
                    <option value="luxury">£200+</option>
                  </select>
                </div>
                <div style={s.selectGroup}>
                  <span style={s.smallLabel}>Gender Preference</span>
                  <select style={s.select} value={preferredGender} onChange={e => setPreferredGender(e.target.value)}>
                    <option value="">Select...</option>
                    <option value="Masculine">Masculine</option>
                    <option value="Feminine">Feminine</option>
                    <option value="Unisex">Unisex</option>
                    <option value="No preference">No preference</option>
                  </select>
                </div>
              </div>
            </div>

            <button style={s.saveBtn} onClick={savePreferences}>Save Preferences</button>
            {prefSaved && <p style={s.successMsg}>{prefSaved}</p>}
            {prefError && <p style={s.errorMsg}>{prefError}</p>}
          </div>
        )}

        {/* ── Security ── */}
        {activeTab === 'security' && (
          <div>
            <label style={s.sectionTitle}>Change Email</label>
            <p style={s.securityNote}>A verification link will be sent to your new email address. Your email won't change until you click it.</p>
            <label style={s.smallLabel}>New Email Address</label>
            <input style={s.input} type="email" placeholder="new@email.com" value={newEmail} onChange={e => setNewEmail(e.target.value)} />
            <label style={s.smallLabel}>Confirm Password</label>
            <input style={s.input} type="password" placeholder="Your current password" value={emailPassword} onChange={e => setEmailPassword(e.target.value)} />
            <button style={s.outlineBtn} onClick={saveEmail}>Send Verification</button>
            {emailSaved && <p style={s.successMsg}>{emailSaved}</p>}
            {emailError && <p style={s.errorMsg}>{emailError}</p>}
            <div style={s.sectionDivider} />
            <label style={s.sectionTitle}>Change Password</label>
            <p style={s.securityNote}>Choose a strong password of at least 6 characters.</p>
            <label style={s.smallLabel}>Current Password</label>
            <input style={s.input} type="password" placeholder="Current password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} />
            <label style={s.smallLabel}>New Password</label>
            <input style={s.input} type="password" placeholder="New password" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
            <label style={s.smallLabel}>Confirm New Password</label>
            <input style={s.input} type="password" placeholder="Confirm new password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
            <button style={s.outlineBtn} onClick={savePassword}>Update Password</button>
            {pwSaved && <p style={s.successMsg}>{pwSaved}</p>}
            {pwError && <p style={s.errorMsg}>{pwError}</p>}
            <div style={s.sectionDivider} />
            <label style={s.sectionTitle}>Delete Account</label>
            <p style={s.securityNote}>Permanently deletes your account, library, journal and all data. This cannot be undone.</p>
            {deleteStep === 0 && (
              <button style={s.dangerBtn} onClick={() => setDeleteStep(1)}>Delete My Account</button>
            )}
            {deleteStep === 1 && (
              <div style={{ background: '#0f0f0f', border: '1px solid #7a3030', padding: '24px', maxWidth: isMobile ? '100%' : '480px' }}>
                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem', fontWeight: 300, color: '#f5f0eb', marginBottom: '12px' }}>Are you sure?</p>
                <p style={{ ...s.securityNote, marginBottom: '24px' }}>This will permanently delete your account, your entire library, all journal entries, and your fragrance history. There is no way to recover this data.</p>
                <div style={{ display: 'flex', gap: '12px', flexDirection: isMobile ? 'column' : 'row' }}>
                  <button style={s.dangerBtn} onClick={() => setDeleteStep(2)}>Yes, Continue</button>
                  <button style={s.outlineBtn} onClick={resetDelete}>Cancel</button>
                </div>
              </div>
            )}
            {deleteStep === 2 && (
              <div style={{ background: '#0f0f0f', border: '1px solid #7a3030', padding: '24px', maxWidth: isMobile ? '100%' : '480px' }}>
                <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem', fontWeight: 300, color: '#f5f0eb', marginBottom: '20px' }}>Final confirmation</p>
                <label style={s.smallLabel}>Type DELETE to confirm</label>
                <input style={{ ...s.input, borderColor: deleteConfirmText === 'DELETE' ? '#7a3030' : '#2a2a2a' }} type="text" placeholder="DELETE" value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} />
                <label style={s.smallLabel}>Enter your password</label>
                <input style={s.input} type="password" placeholder="Your password" value={deletePassword} onChange={e => setDeletePassword(e.target.value)} />
                <div style={{ display: 'flex', gap: '12px', flexDirection: isMobile ? 'column' : 'row' }}>
                  <button
                    style={{ ...s.dangerBtn, opacity: deleteConfirmText === 'DELETE' && deletePassword ? 1 : 0.4, cursor: deleteConfirmText === 'DELETE' && deletePassword ? 'pointer' : 'not-allowed' }}
                    onClick={handleDeleteAccount}
                    disabled={deleting || deleteConfirmText !== 'DELETE' || !deletePassword}
                  >
                    {deleting ? 'Deleting...' : 'Permanently Delete Account'}
                  </button>
                  <button style={s.outlineBtn} onClick={resetDelete}>Cancel</button>
                </div>
                {deleteError && <p style={s.errorMsg}>{deleteError}</p>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;